import React, { useEffect, useMemo, useState } from 'react';
import { APIProvider, Map as GoogleMap, AdvancedMarker, Pin } from '@vis.gl/react-google-maps';
import DialogContent from '@mui/material/DialogContent';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ShareIcon from '@mui/icons-material/IosShare';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DirectionsIcon from '@mui/icons-material/Directions';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import StreetviewIcon from '@mui/icons-material/Streetview';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import NightsStayOutlinedIcon from '@mui/icons-material/NightsStayOutlined';
import WbTwilightOutlinedIcon from '@mui/icons-material/WbTwilightOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import EventIcon from '@mui/icons-material/Event';
import HistoryIcon from '@mui/icons-material/History';
import LocalFloristOutlinedIcon from '@mui/icons-material/LocalFloristOutlined';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import BeachAccessOutlinedIcon from '@mui/icons-material/BeachAccessOutlined';
import ParkOutlinedIcon from '@mui/icons-material/ParkOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LuggageOutlinedIcon from '@mui/icons-material/LuggageOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useDispatch, useSelector } from 'react-redux';
import { setDialogOpen, setSelectedEvent } from '../../store/Actions';
import { Divider } from '../common/Divider';
import { MyAvatar } from '../common/MyAvatar';
import { RichTextEditor } from '../common/richtexteditor/RichTextEditor';
import './ViewEvent.scss';

// Same key + mapId the main MapView uses. Loading the API a second time is
// safe — @vis.gl/react-google-maps deduplicates the underlying script.
const MAPS_API_KEY = 'AIzaSyC0Fwcf-NgdobThTxJbi0fdfpMA_qY0dcs';
const MAPS_MAP_ID = 'bf51a910020fa25a';

// ---------- date helpers ----------
const toDate = (value) => {
  if(!value) return null;
  if(value instanceof Date) return value;
  if(typeof value.toDate === 'function') return value.toDate();
  return new Date(value);
};

const combineDateAndTime = (dateValue, timeValue) => {
  const date = toDate(dateValue);
  if(!date) return null;
  const dateTime = new Date(date);
  const time = timeValue ? toDate(timeValue) : null;
  if(time) dateTime.setHours(time.getHours(), time.getMinutes(), 0, 0);
  else dateTime.setHours(0, 0, 0, 0);
  return dateTime;
};

const getDateString = (date, time) => {
  const dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
  const timeOptions = {hour: '2-digit', minute: '2-digit'};
  date = date instanceof Date ? date : date.toDate();
  let strDate = date.toLocaleDateString('en-US', dateOptions);
  if(time){
    time = time instanceof Date ? time : time.toDate();
    strDate += ' · ' + time.toLocaleTimeString([], timeOptions);
  }
  return strDate;
};

const formatRelativeTime = (date) => {
  if(!date) return '';
  const now = new Date();
  const diffMs = now - date;
  const futurePrefix = diffMs < 0 ? 'in ' : '';
  const pastSuffix = diffMs < 0 ? '' : ' ago';
  const abs = Math.abs(diffMs);
  const days = Math.round(abs / (1000 * 60 * 60 * 24));
  if(days < 1){
    const hours = Math.max(1, Math.round(abs / (1000 * 60 * 60)));
    return `${futurePrefix}${hours} hour${hours === 1 ? '' : 's'}${pastSuffix}`;
  }
  if(days < 30){
    return `${futurePrefix}${days} day${days === 1 ? '' : 's'}${pastSuffix}`;
  }
  const months = Math.round(days / 30.44);
  if(months < 12){
    return `${futurePrefix}${months} month${months === 1 ? '' : 's'}${pastSuffix}`;
  }
  const years = Math.floor(days / 365.25);
  const remMonths = Math.round((days - years * 365.25) / 30.44);
  if(years < 5 && remMonths > 0){
    return `${futurePrefix}${years} yr${years === 1 ? '' : 's'} ${remMonths} mo${pastSuffix}`;
  }
  return `${futurePrefix}${years} year${years === 1 ? '' : 's'}${pastSuffix}`;
};

const getTimeOfDay = (time) => {
  if(!time) return null;
  const t = toDate(time);
  if(!t) return null;
  const h = t.getHours();
  if(h < 5)  return {label: 'Late night', icon: <NightsStayOutlinedIcon/>};
  if(h < 12) return {label: 'Morning', icon: <WbTwilightOutlinedIcon/>};
  if(h < 17) return {label: 'Afternoon', icon: <WbSunnyOutlinedIcon/>};
  if(h < 21) return {label: 'Evening', icon: <WbTwilightOutlinedIcon/>};
  return {label: 'Night', icon: <NightsStayOutlinedIcon/>};
};

// Northern-hemisphere season for the given date.
const getSeason = (date) => {
  if(!date) return null;
  const m = date.getMonth();
  const d = date.getDate();
  // Approx astronomical seasons.
  if((m === 2 && d >= 20) || m === 3 || m === 4 || (m === 5 && d < 21))
    return {label: 'Spring', icon: <LocalFloristOutlinedIcon/>};
  if((m === 5 && d >= 21) || m === 6 || m === 7 || (m === 8 && d < 23))
    return {label: 'Summer', icon: <BeachAccessOutlinedIcon/>};
  if((m === 8 && d >= 23) || m === 9 || m === 10 || (m === 11 && d < 21))
    return {label: 'Autumn', icon: <ParkOutlinedIcon/>};
  return {label: 'Winter', icon: <AcUnitIcon/>};
};

const formatDuration = (startAt, endAt) => {
  if(!startAt || !endAt) return null;
  const ms = endAt - startAt;
  if(ms <= 0) return null;
  const minutes = Math.round(ms / (1000 * 60));
  if(minutes < 60) return `${minutes} min`;
  const hours = Math.round(minutes / 60);
  if(hours < 24) return `${hours} hr${hours === 1 ? '' : 's'}`;
  const days = Math.round(hours / 24);
  if(days < 31) return `${days} day${days === 1 ? '' : 's'}`;
  const months = Math.round(days / 30.44);
  if(months < 24) return `${months} month${months === 1 ? '' : 's'}`;
  const years = (days / 365.25).toFixed(1);
  return `${years} years`;
};

// Walk descriptionJSON for any image src values, in document order.
const extractDescriptionImageUrls = (descriptionJSON) => {
  if(!descriptionJSON || typeof descriptionJSON !== 'object') return [];
  const urls = [];
  const walk = (node) => {
    if(!node) return;
    if(node.type === 'image' && node.attrs && node.attrs.src) urls.push(node.attrs.src);
    if(Array.isArray(node.content)) node.content.forEach(walk);
  };
  walk(descriptionJSON);
  return urls;
};

// ---------- the modal ----------
export function ViewEvent() {
  const dialogData = useSelector(state=>state.dialogData);
  const event = dialogData.event;
  const viewingUserAccount = useSelector(state=>state.viewingUserAccount);
  const loggedInUserAccount = useSelector(state=>state.loggedInUserAccount);
  const userCategories = useSelector(state=>state.viewingUserCategories);
  const userTrips = useSelector(state=>state.viewingUserTrips);
  const userFriends = useSelector(state=>state.viewingUserFriends);
  const userTags = useSelector(state=>state.viewingUserTags);
  const userEvents = useSelector(state=>state.viewingUserEvents);
  const placesService = useSelector(state=>state.placesService);

  const [placePhotoUrls, setPlacePhotoUrls] = useState([]);
  const [placeMapUrl, setPlaceMapUrl] = useState('');
  const [shareCopied, setShareCopied] = useState(false);
  const [activePhoto, setActivePhoto] = useState(null);
  const dispatch = useDispatch();
  const viewingLoggedInUser = viewingUserAccount && loggedInUserAccount && viewingUserAccount.id === loggedInUserAccount.id;
  const category = userCategories?.[event.categoryId];
  const accentColor = category?.pinColor || '#6366f1';

  const startAt = useMemo(() => combineDateAndTime(event.startDate, event.startTime), [event.startDate, event.startTime]);
  const endAt   = useMemo(() => combineDateAndTime(event.endDate, event.endTime), [event.endDate, event.endTime]);
  const dayOfWeekShort = startAt ? startAt.toLocaleDateString('en-US', {weekday: 'short'}) : null;
  const calendarDate = startAt ? startAt.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'}) : null;
  const formattedStartTime = event.startTime
    ? toDate(event.startTime).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})
    : null;
  const timeOfDay = getTimeOfDay(event.startTime);
  const season = getSeason(startAt);
  const relativeTime = formatRelativeTime(startAt);
  const duration = formatDuration(startAt, endAt);

  // Pull additional details from Google Places: photos to fall back on,
  // and the canonical Google Maps URL. We only fetch when needed.
  useEffect(() => {
    if(!placesService || !event.location?.placeId) return;
    placesService.getDetails({placeId: event.location.placeId}, (result) => {
      if(!result) return;
      if(result.url) setPlaceMapUrl(result.url);
      if(Array.isArray(result.photos)){
        const urls = result.photos.slice(0, 5).map(p => {
          try { return p.getUrl({maxWidth: 1200}); } catch(e){ return null; }
        }).filter(Boolean);
        setPlacePhotoUrls(urls);
      }
    });
  }, [placesService, event.location?.placeId]);

  // ---------- derived collections ----------
  const heroImageUrl = event.coverPicUrl || placePhotoUrls[0] || '';

  const descriptionImages = useMemo(
    () => extractDescriptionImageUrls(event.descriptionJSON),
    [event.descriptionJSON]
  );
  const galleryUrls = useMemo(() => {
    const seen = new Set();
    const result = [];
    [...descriptionImages, ...placePhotoUrls].forEach(url => {
      if(!url) return;
      if(url === event.coverPicUrl) return;
      if(seen.has(url)) return;
      seen.add(url);
      result.push(url);
    });
    return result.slice(0, 8);
  }, [descriptionImages, placePhotoUrls, event.coverPicUrl]);

  const tripList = useMemo(() => (event.tripIds || [])
    .map(tripId => userTrips?.[tripId])
    .filter(Boolean), [event.tripIds, userTrips]);

  const tagList = useMemo(() => (event.tagIds || [])
    .map(tagId => userTags?.[tagId])
    .filter(Boolean), [event.tagIds, userTags]);

  const friendList = useMemo(() => (event.friendIds || [])
    .map(friendId => userFriends?.[friendId])
    .filter(Boolean), [event.friendIds, userFriends]);

  // "Day X of trip"-style fact, plus the chronological position within the trip.
  const tripContext = useMemo(() => {
    if(!startAt || !userEvents) return null;
    const primaryTripId = (event.tripIds || [])[0];
    if(!primaryTripId) return null;
    const trip = userTrips?.[primaryTripId];
    if(!trip) return null;

    const tripEvents = Object.values(userEvents).filter(e =>
      (e.tripIds || []).includes(primaryTripId)
    );
    const dated = tripEvents
      .map(e => ({event: e, at: combineDateAndTime(e.startDate, e.startTime)}))
      .filter(d => d.at)
      .sort((a, b) => a.at - b.at);
    if(dated.length === 0) return null;
    const tripStart = dated[0].at;
    const tripEnd = dated[dated.length - 1].at;
    const dayNumber = Math.max(1, Math.floor((startAt - tripStart) / (1000 * 60 * 60 * 24)) + 1);
    // Total length of the trip in days, inclusive of both endpoints.
    const totalDays = Math.max(1, Math.floor((tripEnd - tripStart) / (1000 * 60 * 60 * 24)) + 1);
    const indexInTrip = dated.findIndex(d => d.event.id === event.id);

    return {
      trip,
      dayNumber,
      totalDays,
      indexInTrip: indexInTrip >= 0 ? indexInTrip : 0,
      total: dated.length,
      tripStart,
      tripEnd,
    };
  }, [startAt, event.id, event.tripIds, userEvents, userTrips]);

  // Other events in the same trip (or, if no trip, near in time) — the strip
  // at the bottom that lets you bounce between adjacent memories.
  const relatedEvents = useMemo(() => {
    if(!userEvents || !startAt) return [];
    const all = Object.values(userEvents).filter(e => e.id !== event.id);

    const tripIdSet = new Set(event.tripIds || []);
    let candidates = [];
    if(tripIdSet.size > 0){
      candidates = all.filter(e => (e.tripIds || []).some(t => tripIdSet.has(t)));
    }
    if(candidates.length < 4){
      // If we don't have enough trip-mates, pad with events near in time.
      const window = 1000 * 60 * 60 * 24 * 30; // 30 days
      const more = all.filter(e => {
        if(candidates.includes(e)) return false;
        const at = combineDateAndTime(e.startDate, e.startTime);
        return at && Math.abs(at - startAt) <= window;
      });
      candidates = [...candidates, ...more];
    }
    return candidates
      .map(e => ({event: e, at: combineDateAndTime(e.startDate, e.startTime)}))
      .filter(d => d.at)
      .sort((a, b) => a.at - b.at)
      .slice(0, 8);
  }, [userEvents, event.id, event.tripIds, startAt]);

  // ---------- handlers ----------
  const close = () => dispatch(setDialogOpen(''));
  const edit = () => {
    dispatch(setSelectedEvent(null));
    dispatch(setDialogOpen('Edit Event', {event}));
  };
  const share = async () => {
    const summary = `${event.title} — ${getDateString(event.startDate, event.startTime)}${event.location?.description ? ` @ ${event.location.description}` : ''}`;
    try {
      await navigator.clipboard.writeText(summary);
      setShareCopied(true);
      setTimeout(()=>setShareCopied(false), 1800);
    } catch(e) {
      // ignore – clipboard may be blocked in some browsers
    }
  };
  const openRelated = (e) => {
    dispatch(setDialogOpen('View Event', {event: e}));
  };

  const directionsUrl = event.location?.coordinates
    ? `https://www.google.com/maps/dir/?api=1&destination=${event.location.coordinates.lat},${event.location.coordinates.lng}${event.location.placeId ? `&destination_place_id=${event.location.placeId}` : ''}`
    : null;
  const streetViewUrl = event.location?.coordinates
    ? `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${event.location.coordinates.lat},${event.location.coordinates.lng}`
    : null;
  const mapUrl = placeMapUrl || (event.location?.coordinates
    ? `https://www.google.com/maps/search/?api=1&query=${event.location.coordinates.lat},${event.location.coordinates.lng}${event.location.placeId ? `&query_place_id=${event.location.placeId}` : ''}`
    : null);

  // ---------- render ----------
  return (
    <>
      <DialogContent className="view-event__content">

        {/* Sticky controls — pinned to the top-right of the dialog so
            share/edit/close stay reachable while the user scrolls
            through the body. Lives outside the hero so its sticky
            positioning is anchored to the DialogContent scroll
            container, not the hero's box. */}
        <div className="view-event__hero-actions">
          <button
            type="button"
            className="view-event__hero-icon-btn"
            aria-label={shareCopied ? 'Copied' : 'Share event'}
            title={shareCopied ? 'Copied' : 'Share'}
            onClick={share}
          >
            <ShareIcon/>
          </button>
          {viewingLoggedInUser &&
            <button
              type="button"
              className="view-event__hero-icon-btn view-event__hero-icon-btn--accent"
              aria-label="Edit event"
              title="Edit"
              onClick={edit}
            >
              <EditIcon/>
            </button>
          }
          <button
            type="button"
            className="view-event__hero-icon-btn"
            aria-label="Close event"
            title="Close"
            onClick={close}
          >
            <CloseIcon/>
          </button>
        </div>

        {/* HERO — modeled after the entity detail views (FriendDetailView,
            TripDetailView, etc.). Glow background + centered hero content
            with cover, tagline, and big title. */}
        <section
          className="view-event__hero"
          style={{'--hero-accent': accentColor}}
        >
          <div
            className="view-event__hero-glow"
            style={{'--hero-accent': accentColor}}
            aria-hidden="true"
          />

          <div className="view-event__hero-content">
            {heroImageUrl ? (
              <div
                className="view-event__hero-cover"
                style={{backgroundImage: `url(${heroImageUrl})`}}
                aria-label={`${event.title} cover photo`}
              />
            ) : (
              <div
                className="view-event__hero-cover view-event__hero-cover--placeholder"
                style={{background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}cc 100%)`}}
                aria-hidden="true"
              >
                {event.emoji || category?.defaultEmoji || '✨'}
              </div>
            )}
            <div className="view-event__hero-icon" aria-hidden="true">
              {event.emoji || category?.defaultEmoji || '✨'}
            </div>
            {category &&
              <div className="view-event__hero-tagline" style={{color: accentColor}}>
                {category.name}
              </div>
            }
            <h1 className="view-event__hero-title">{event.title}</h1>
          </div>
        </section>

        {shareCopied &&
          <div className="view-event__share-toast" role="status" aria-live="polite">
            Copied to clipboard
          </div>
        }

        {/* BODY — two-column split. Left column holds everything except
            the story (stats, location, trip, people, connections, gallery,
            related events). Right column holds the full-height story. */}
        <div className={`view-event__body ${event.descriptionHTML ? 'view-event__body--two-col' : ''}`.trim()}>
          <div className="view-event__left">

        {/* QUICK STATS */}
        <section className="view-event__stats">
          {calendarDate &&
            <div className="view-event__stat">
              <EventIcon/>
              <div>
                <strong>{calendarDate}</strong>
                <span>{dayOfWeekShort ? `${dayOfWeekShort}${formattedStartTime ? ` · ${formattedStartTime}` : ''}` : 'Date'}</span>
              </div>
            </div>
          }
          {relativeTime &&
            <div className="view-event__stat">
              <HistoryIcon/>
              <div>
                <strong>{relativeTime}</strong>
                <span>When it happened</span>
              </div>
            </div>
          }
          {timeOfDay &&
            <div className="view-event__stat">
              {timeOfDay.icon}
              <div>
                <strong>{timeOfDay.label}</strong>
                <span>{formattedStartTime || 'Time of day'}</span>
              </div>
            </div>
          }
          {season &&
            <div className="view-event__stat">
              {season.icon}
              <div>
                <strong>{season.label}</strong>
                <span>Season</span>
              </div>
            </div>
          }
          {duration &&
            <div className="view-event__stat">
              <HourglassEmptyIcon/>
              <div>
                <strong>{duration}</strong>
                <span>Duration</span>
              </div>
            </div>
          }
          {tripContext &&
            <div className="view-event__stat">
              <LuggageOutlinedIcon/>
              <div>
                <strong>Day {tripContext.dayNumber} of {tripContext.totalDays}</strong>
                <span>{tripContext.trip.name}</span>
              </div>
            </div>
          }
          {friendList.length > 0 &&
            <div className="view-event__stat">
              <GroupsOutlinedIcon/>
              <div>
                <strong>{friendList.length + 1} people</strong>
                <span>Were there</span>
              </div>
            </div>
          }
        </section>

        {/* PEOPLE — placed above the map so the social context lands first */}
            <div className="view-event__card">
              <div className="view-event__card-header">
                <GroupsOutlinedIcon style={{color: accentColor}}/>
                <h3>Who was there</h3>
              </div>
              <div className="view-event__people">
                <div className="view-event__person">
                  <MyAvatar userAccount={viewingUserAccount} style={{height:64, width:64}}/>
                  <span>{viewingUserAccount?.displayName || 'You'}</span>
                </div>
                {friendList.map(friend => (
                  <div key={friend.id} className="view-event__person">
                    <MyAvatar userAccount={friend} style={{height:64, width:64}}/>
                    <span>{friend.name}</span>
                  </div>
                ))}
                {friendList.length === 0 &&
                  <div className="view-event__people-solo">A solo memory</div>
                }
              </div>
            </div>

        {/* LOCATION + MAP */}
            <div className="view-event__card">
              <div className="view-event__card-header">
                <LocationOnIcon style={{color: accentColor}}/>
                <h3>Where it happened</h3>
              </div>
              <div className="view-event__map">
                {event.location?.coordinates ? (
                  <APIProvider apiKey={MAPS_API_KEY} libraries={['marker']}>
                    <GoogleMap
                      key={event.id}
                      mapId={MAPS_MAP_ID}
                      defaultCenter={event.location.coordinates}
                      defaultZoom={13}
                      gestureHandling="cooperative"
                      disableDefaultUI={true}
                      zoomControl={false}
                      keyboardShortcuts={false}
                      style={{width: '100%', height: '100%'}}
                    >
                      <AdvancedMarker position={event.location.coordinates}>
                        <Pin background={accentColor} borderColor={accentColor} glyphColor="#ffffff">
                          <span style={{fontSize: 16}}>
                            {event.emoji || category?.defaultEmoji || '📍'}
                          </span>
                        </Pin>
                      </AdvancedMarker>
                    </GoogleMap>
                  </APIProvider>
                ) : (
                  <div className="view-event__map-fallback" style={{backgroundColor: `${accentColor}22`}}>
                    <LocationOnIcon style={{color: accentColor}}/>
                    <span>No coordinates for this place</span>
                  </div>
                )}
              </div>
              {event.location?.description &&
                <div className="view-event__address">{event.location.description}</div>
              }
              {event.location?.coordinates &&
                <div className="view-event__coords">
                  {event.location.coordinates.lat.toFixed(4)}°, {event.location.coordinates.lng.toFixed(4)}°
                </div>
              }
              <div className="view-event__map-actions">
                {mapUrl &&
                  <a className="view-event__map-action" href={mapUrl} target="_blank" rel="noreferrer">
                    <OpenInNewIcon fontSize="small"/> Open in Maps
                  </a>
                }
                {directionsUrl &&
                  <a className="view-event__map-action" href={directionsUrl} target="_blank" rel="noreferrer">
                    <DirectionsIcon fontSize="small"/> Directions
                  </a>
                }
                {streetViewUrl &&
                  <a className="view-event__map-action" href={streetViewUrl} target="_blank" rel="noreferrer">
                    <StreetviewIcon fontSize="small"/> Street View
                  </a>
                }
              </div>
            </div>

            {tripContext &&
              <div className="view-event__card view-event__trip-card">
                <div className="view-event__card-header">
                  <LuggageOutlinedIcon style={{color: accentColor}}/>
                  <h3>Part of a trip</h3>
                </div>
                <div className="view-event__trip-name">{tripContext.trip.name}</div>
                <div className="view-event__trip-dates">
                  {tripContext.tripStart.toLocaleDateString('en-US', {month:'short', day:'numeric', year:'numeric'})} – {tripContext.tripEnd.toLocaleDateString('en-US', {month:'short', day:'numeric', year:'numeric'})}
                </div>
                <div className="view-event__trip-progress">
                  <div className="view-event__trip-progress-bar">
                    <div
                      className="view-event__trip-progress-fill"
                      style={{
                        width: `${tripContext.total > 1 ? (tripContext.indexInTrip / (tripContext.total - 1)) * 100 : 0}%`,
                        backgroundColor: accentColor,
                      }}
                    />
                    <div
                      className="view-event__trip-progress-marker"
                      style={{
                        left: `${tripContext.total > 1 ? (tripContext.indexInTrip / (tripContext.total - 1)) * 100 : 0}%`,
                        backgroundColor: accentColor,
                      }}
                    />
                  </div>
                  <div className="view-event__trip-progress-labels">
                    <span>Start</span>
                    <span>{tripContext.indexInTrip + 1} of {tripContext.total} memories</span>
                    <span>End</span>
                  </div>
                </div>
              </div>
            }

            {(tripList.length > 0 || tagList.length > 0) &&
              <div className="view-event__card">
                <div className="view-event__card-header">
                  <LocalOfferOutlinedIcon style={{color: accentColor}}/>
                  <h3>Connections</h3>
                </div>
                <div className="view-event__chips">
                  {tripList.map(trip => (
                    <span key={trip.id} className="view-event__chip view-event__chip--trip">
                      <LuggageOutlinedIcon fontSize="small"/> {trip.name}
                    </span>
                  ))}
                  {tagList.map(tag => (
                    <span key={tag.id} className="view-event__chip view-event__chip--tag">
                      #{tag.name}
                    </span>
                  ))}
                </div>
              </div>
            }

            {/* PHOTO GALLERY */}
            {galleryUrls.length > 0 &&
              <section className="view-event__card view-event__gallery-section">
                <div className="view-event__card-header">
                  <PhotoLibraryOutlinedIcon style={{color: accentColor}}/>
                  <h3>Photos</h3>
                  <span className="view-event__count">{galleryUrls.length}</span>
                </div>
                <div className="view-event__gallery">
                  {galleryUrls.map((url, i) => (
                    <button
                      key={url + i}
                      type="button"
                      className="view-event__gallery-item"
                      onClick={() => setActivePhoto(url)}
                      aria-label={`View photo ${i+1}`}
                    >
                      <img src={url} alt="" loading="lazy"/>
                    </button>
                  ))}
                </div>
              </section>
            }

            {/* RELATED EVENTS */}
            {relatedEvents.length > 0 &&
              <section className="view-event__card view-event__related-section">
                <div className="view-event__card-header">
                  <TimelineIcon style={{color: accentColor}}/>
                  <h3>{(event.tripIds || []).length > 0 ? 'Other moments from this trip' : 'Around the same time'}</h3>
                  <span className="view-event__count">{relatedEvents.length}</span>
                </div>
                <Divider/>
                <div className="view-event__related">
                  {relatedEvents.map(({event: rel, at}) => {
                    const relCategory = userCategories?.[rel.categoryId];
                    const relAccent = relCategory?.pinColor || '#94a3b8';
                    return (
                      <button
                        key={rel.id}
                        type="button"
                        className="view-event__related-card"
                        onClick={() => openRelated(rel)}
                      >
                        <div
                          className="view-event__related-thumb"
                          style={{backgroundColor: relAccent}}
                        >
                          {rel.coverPicUrl
                            ? <img src={rel.coverPicUrl} alt="" loading="lazy"/>
                            : <span>{rel.emoji || relCategory?.defaultEmoji || '✨'}</span>
                          }
                        </div>
                        <div className="view-event__related-body">
                          <div className="view-event__related-date">
                            {at.toLocaleDateString('en-US', {month: 'short', day: 'numeric', year: 'numeric'})}
                          </div>
                          <div className="view-event__related-title">{rel.title}</div>
                          {rel.location?.description &&
                            <div className="view-event__related-location">{rel.location.description}</div>
                          }
                        </div>
                      </button>
                    );
                  })}
                </div>
              </section>
            }
          </div>

          {/* RIGHT — full-height story */}
          {event.descriptionHTML &&
            <div className="view-event__right">
              <div className="view-event__card view-event__card--description">
                <div className="view-event__card-header">
                  <StarBorderRoundedIcon style={{color: accentColor}}/>
                  <h3>The story</h3>
                </div>
                <div className="view-event__description">
                  <RichTextEditor
                    readonly={true}
                    contentHTML={event.descriptionHTML.replaceAll('<img ', '<img loading="lazy"')}
                  />
                </div>
              </div>
            </div>
          }
        </div>
      </DialogContent>

      {/* Lightbox */}
      {activePhoto &&
        <div
          className="view-event__lightbox"
          onClick={() => setActivePhoto(null)}
          role="dialog"
          aria-label="Photo preview"
        >
          <img src={activePhoto} alt=""/>
          <button
            type="button"
            className="view-event__lightbox-close"
            onClick={() => setActivePhoto(null)}
            aria-label="Close photo"
          >
            <CloseIcon/>
          </button>
        </div>
      }
    </>
  );
}
